<template>
    <div class="filter-container">
        <!-- Filter Toggle Button -->
        <button
            @click="handleFilterClick"
            class="filter-toggle-btn"
            :class="{ 'disabled': disabled }"
            :title="disabled ? 'Upgrade to Student plan to filter questions' : 'Filter questions'"
        >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <line x1="4" y1="6" x2="20" y2="6" stroke-width="2"/>
                <line x1="4" y1="12" x2="20" y2="12" stroke-width="2"/>
                <line x1="4" y1="18" x2="20" y2="18" stroke-width="2"/>
                <circle cx="7" cy="6" r="2" fill="currentColor"/>
                <circle cx="17" cy="12" r="2" fill="currentColor"/>
                <circle cx="7" cy="18" r="2" fill="currentColor"/>
            </svg>
            Filter Questions
            <svg v-if="disabled" class="lock-icon" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
            </svg>
            <span v-else-if="activeFilterCount" class="filter-badge">{{ activeFilterCount }}</span>
        </button>

        <!-- Filter Modal -->
        <div v-if="showModal" class="modal-overlay" @click.self="showModal = false">
            <div class="modal-content">
                <div class="modal-header">
                    <h2>Filter Questions</h2>
                    <div class="filter-mode-toggle">
                        <span :class="{ active: filterMode === 'AND' }">AND</span>
                        <label class="toggle-switch">
                            <input type="checkbox" v-model="isOrMode" />
                            <span class="toggle-slider"></span>
                        </label>
                        <span :class="{ active: filterMode === 'OR' }">OR</span>
                    </div>
                    <button @click="showModal = false" class="close-btn">&times;</button>
                </div>

                <!-- Main Tabs -->
                <div class="tabs">
                    <button 
                        v-for="tab in mainTabs" 
                        :key="tab"
                        @click="activeTab = tab"
                        :class="['tab-btn', { active: activeTab === tab }]"
                    >
                        {{ tab }}
                    </button>
                </div>

                <!-- Tab Content -->
                <div class="tab-content">
                    <!-- Topics Tab -->
                    <div v-if="activeTab === 'Topics'" class="topics-section">
                        <!-- Module Subtabs -->
                        <div class="subtabs">
                            <button 
                                v-for="module in modules" 
                                :key="module.id"
                                @click="activeModule = module.id"
                                :class="['subtab-btn', { active: activeModule === module.id }]"
                            >
                                {{ module.name }}
                            </button>
                        </div>

                        <!-- Select All Button -->
                        <div class="select-all-container">
                            <button 
                                @click="toggleAllTopicsInModule"
                                class="select-all-btn"
                            >
                                {{ areAllTopicsSelected ? 'Deselect All' : 'Select All' }} in {{ getCurrentModuleName }}
                            </button>
                        </div>

                        <!-- Topic Checkboxes -->
                        <div class="checkbox-grid">
                            <label 
                                v-for="topic in currentModuleTopics" 
                                :key="topic.value"
                                class="checkbox-label"
                            >
                                <input 
                                    type="checkbox" 
                                    :value="topic.value"
                                    v-model="selectedTopics"
                                />
                                <span>{{ topic.label }}</span>
                            </label>
                        </div>
                    </div>

                    <!-- Difficulty Tab -->
                    <div v-if="activeTab === 'Difficulty'" class="difficulty-section">
                        <div class="checkbox-list">
                            <label 
                                v-for="diff in difficulties" 
                                :key="diff.value"
                                class="checkbox-label large"
                            >
                                <input 
                                    type="checkbox" 
                                    :value="diff.value"
                                    v-model="selectedDifficulties"
                                />
                                <span class="difficulty-badge" :data-difficulty="diff.value">
                                    {{ diff.label }}
                                </span>
                            </label>
                        </div>
                    </div>

                    <!-- Question Type Tab -->
                    <div v-if="activeTab === 'Question Type'" class="type-section">
                        <div class="checkbox-list">
                            <label 
                                v-for="type in questionTypes" 
                                :key="type.value"
                                class="checkbox-label large"
                            >
                                <input 
                                    type="checkbox" 
                                    :value="type.value"
                                    v-model="selectedTypes"
                                />
                                <span>{{ type.label }}</span>
                            </label>
                        </div>
                    </div>
                </div>

                <!-- Modal Footer -->
                <div class="modal-footer">
                    <button @click="clearFilters" class="clear-btn">
                        Clear All Filters
                    </button>
                    <button @click="applyFilters" class="apply-btn">
                        Apply Filters
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue';
import { topics } from '@/utils/topicData';

export default {
    name: 'QuestionFilter',
    props: {
        currentQuestion: {
            type: Object,
            default: null
        },
        activeFilters: {
            type: Object,
            default: () => ({ topics: [], difficulties: [], types: [], filterMode: 'AND' })
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    emits: ['filter-applied'],
    setup(props, { emit }) {
        // ==================== STATE ====================
        const showModal = ref(false);
        const activeTab = ref('Topics');
        const activeModule = ref('M1');
        const isOrMode = ref(false);

        // Computed filter mode based on toggle
        const filterMode = computed(() => isOrMode.value ? 'OR' : 'AND');

        // ==================== CONSTANTS ====================
        const mainTabs = ['Topics', 'Difficulty', 'Question Type'];

        const modules = [
            { id: 'M1', name: 'Module 1' },
            { id: 'M2', name: 'Module 2' },
            { id: 'M3', name: 'Module 3' },
            { id: 'M4', name: 'Module 4' },
            { id: 'M5', name: 'Module 5' },
            { id: 'M6', name: 'Module 6' },
            { id: 'M7', name: 'Module 7' },
            { id: 'M8', name: 'Module 8' }
        ];

        const difficulties = [
            { value: 'band_1', label: 'Band 1' },
            { value: 'band_2', label: 'Band 2' },
            { value: 'band_3', label: 'Band 3' },
            { value: 'band_4', label: 'Band 4' },
            { value: 'band_5', label: 'Band 5' },
            { value: 'band_6', label: 'Band 6' }
        ];

        const questionTypes = [
            { value: 'multiple_choice', label: 'Multiple Choice' },
            { value: 'short_answer', label: 'Short Answer' },
            { value: 'calculation', label: 'Calculation' },
            { value: 'equation_balancing', label: 'Equation Balancing' },
            { value: 'structural_formula', label: 'Structural Formula' },
            { value: 'explain', label: 'Explain' }
        ];

        // Initialize state with all options selected (default to allow all)
        const getAllTopicValues = () => {
            return Object.values(topics).flat().map(t => t.value);
        };
        
        const selectedTopics = ref(getAllTopicValues());
        const selectedDifficulties = ref(difficulties.map(d => d.value));
        const selectedTypes = ref(questionTypes.map(t => t.value));

        // ==================== COMPUTED ====================
        const currentModuleTopics = computed(() => {
            return topics[activeModule.value] || [];
        });

        const getCurrentModuleName = computed(() => {
            return modules.find(m => m.id === activeModule.value)?.name || '';
        });

        const areAllTopicsSelected = computed(() => {
            const moduleTopics = currentModuleTopics.value.map(t => t.value);
            return moduleTopics.every(topic => selectedTopics.value.includes(topic));
        });

        const activeFilterCount = computed(() => {
            return selectedTopics.value.length + 
                selectedDifficulties.value.length + 
                selectedTypes.value.length;
        });

        // ==================== METHODS ====================
        const toggleAllTopicsInModule = () => {
            const moduleTopics = currentModuleTopics.value.map(t => t.value);
            
            if (areAllTopicsSelected.value) {
                // Deselect all topics in this module
                selectedTopics.value = selectedTopics.value.filter(
                    topic => !moduleTopics.includes(topic)
                );
            } else {
                // Select all topics in this module
                const newTopics = moduleTopics.filter(
                    topic => !selectedTopics.value.includes(topic)
                );
                selectedTopics.value = [...selectedTopics.value, ...newTopics];
            }
        };

        const clearFilters = () => {
            selectedTopics.value = [];
            selectedDifficulties.value = [];
            selectedTypes.value = [];
        };

        const currentQuestionMatchesFilters = () => {
            if (!props.currentQuestion) return false;
            
            // Check if any filters are active
            if (activeFilterCount.value === 0) return true;

            const question = props.currentQuestion;
            
            // Check topics
            if (selectedTopics.value.length > 0) {
                const hasMatchingTopic = question.topic?.some(t => 
                    selectedTopics.value.includes(t)
                );
                if (!hasMatchingTopic) return false;
            }
            
            // Check difficulty
            if (selectedDifficulties.value.length > 0) {
                if (!selectedDifficulties.value.includes(question.difficulty)) {
                    return false;
                }
            }
            
            // Check question type
            if (selectedTypes.value.length > 0) {
                if (!selectedTypes.value.includes(question.question_type)) {
                    return false;
                }
            }
            
            return true;
        };

        const syncFromProps = () => {
            // Only sync if parent has active filters (non-empty arrays)
            const hasActiveFilters =
                props.activeFilters.topics.length > 0 ||
                props.activeFilters.difficulties.length > 0 ||
                props.activeFilters.types.length > 0;

            if (hasActiveFilters) {
                selectedTopics.value = [...props.activeFilters.topics];
                selectedDifficulties.value = [...props.activeFilters.difficulties];
                selectedTypes.value = [...props.activeFilters.types];
            }

            // Sync filter mode
            isOrMode.value = props.activeFilters.filterMode === 'OR';
        };

        // Sync state from props when modal opens
        watch(showModal, (isOpen) => {
            if (isOpen) {
                syncFromProps();
            }
        });

        // Also sync on mount in case filters were already set
        onMounted(() => {
            syncFromProps();
        });

        const applyFilters = () => {
            const filters = {
                topics: selectedTopics.value,
                difficulties: selectedDifficulties.value,
                types: selectedTypes.value,
                filterMode: filterMode.value,
                shouldFetchNew: !currentQuestionMatchesFilters()
            };

            emit('filter-applied', filters);
            showModal.value = false;
        };

        const handleFilterClick = () => {
            if (props.disabled) {
                // Could emit an event or show tooltip - for now just don't open
                return;
            }
            showModal.value = true;
        };

        return {
            showModal,
            activeTab,
            activeModule,
            selectedTopics,
            selectedDifficulties,
            selectedTypes,
            isOrMode,
            filterMode,
            mainTabs,
            modules,
            topics,
            difficulties,
            questionTypes,
            currentModuleTopics,
            getCurrentModuleName,
            areAllTopicsSelected,
            activeFilterCount,
            toggleAllTopicsInModule,
            clearFilters,
            applyFilters,
            handleFilterClick
        };
    }
};
</script>

<style scoped>
.filter-container {
    position: relative;
}

.filter-toggle-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 12px; /* Smaller padding to match badge size */
    background: var(--bg-secondary);
    color: var(--text-secondary);
    border: 1px solid rgba(var(--accent-rgb), 0.5);
    cursor: pointer;
    transition: all 0.3s;
    font-size: 12px; /* Match badge font size */
    font-weight: 600; /* Match badge weight */
    position: relative;
}

.filter-toggle-btn:hover:not(.disabled) {
    background: var(--bg-secondary);
    border-color: var(--accent);
}

.filter-toggle-btn.disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.filter-toggle-btn .lock-icon {
    margin-left: 4px;
    opacity: 0.7;
}

.filter-badge {
    position: absolute;
    top: -12px;
    right: -8px;
    background: var(--text-secondary);
    color: var(--bg-primary);
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 600;
}

.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    align-items: flex-start;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
    padding-top: 60px;
    overflow-y: auto;
}

.modal-content {
    background: var(--bg-primary);
    border: 1px solid rgba(var(--accent-rgb), 0.3);
    max-width: 900px;
    width: 100%;
    max-height: calc(100vh - 120px);
    display: flex;
    flex-direction: column;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.8);
    position: sticky;
    top: 60px;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 24px 30px;
    border-bottom: 1px solid rgba(var(--accent-rgb), 0.4);
}

.modal-header h2 {
    color: var(--text-secondary);
    margin: 0;
    font-size: 24px;
}

.filter-mode-toggle {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-left: auto;
    margin-right: 20px;
}

.filter-mode-toggle span {
    color: var(--text-muted, #888);
    font-size: 13px;
    font-weight: 600;
    transition: color 0.3s;
}

.filter-mode-toggle span.active {
    color: var(--accent);
}

.toggle-switch {
    position: relative;
    display: inline-block;
    width: 44px;
    height: 24px;
}

.toggle-switch input {
    opacity: 0;
    width: 0;
    height: 0;
}

.toggle-slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: var(--bg-secondary);
    border: 1px solid rgba(var(--accent-rgb), 0.4);
    border-radius: 24px;
    transition: 0.3s;
}

.toggle-slider:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: 2px;
    bottom: 2px;
    background-color: var(--accent);
    border-radius: 50%;
    transition: 0.3s;
}

.toggle-switch input:checked + .toggle-slider:before {
    transform: translateX(20px);
}

.close-btn {
    background: none;
    border: none;
    color: var(--text-secondary);
    font-size: 32px;
    cursor: pointer;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.2s;
}

.close-btn:hover {
    transform: scale(1.1);
}

.tabs {
    display: flex;
    gap: 4px;
    padding: 20px 30px 0;
    border-bottom: 1px solid rgba(var(--accent-rgb), 0.4);;
}

.tab-btn {
    padding: 12px 24px;
    background: transparent;
    border: none;
    color: var(--accent);
    cursor: pointer;
    transition: all 0.3s;
    border-bottom: 2px solid transparent;
    font-size: 14px;
}

.tab-btn:hover {
    color: var(--accent);
}

.tab-btn.active {
    color: var(--accent);
    border-bottom-color: var(--accent);
}

.tab-content {
    flex: 1;
    overflow-y: auto;
    padding: 30px;
}

.subtabs {
    display: flex;
    gap: 8px;
    margin-bottom: 20px;
    flex-wrap: wrap;
}

.subtab-btn {
    padding: 8px 16px;
    background: var(--bg-secondary);
    border: 1px solid rgba(var(--accent-rgb), 0.4);;
    color: var(--accent);
    cursor: pointer;
    transition: all 0.3s;
    font-size: 13px;
}

.subtab-btn:hover {
    background: var(--accent);
    color: var(--bg-primary);
}

.subtab-btn.active {
    background: var(--accent);
    color: var(--bg-primary);
    border-color: var(--accent);
}

.select-all-container {
    margin-bottom: 20px;
}

.select-all-btn {
    padding: 10px 20px;
    background: var(--bg-secondary);
    border: 1px solid rgba(var(--accent-rgb), 0.4);
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.3s;
    font-size: 13px;
}

.select-all-btn:hover {
    background: var(--accent);
    color: var(--bg-primary);
}

.checkbox-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 12px;
}

.checkbox-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.checkbox-label {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px;
    background: rgba(254, 231, 179, 0.03);
    border: 1px solid rgba(var(--accent-rgb), 0.4);;
    cursor: pointer;
    transition: all 0.3s;
}

.checkbox-label:hover {
    background: rgba(254, 231, 179, 0.08);
    border-color: var(--accent);
}

.checkbox-label.large {
    padding: 14px;
}

.checkbox-label input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: var(--accent);
}

.checkbox-label span {
    color: var(--text-primary);
    font-size: 14px;
}

.difficulty-badge {
    padding: 4px 12px;
    font-weight: 500;
    border-width: 1px;
    border-style: solid;
}

.difficulty-badge[data-difficulty="band_1"] {
    background: rgba(76, 175, 80, 0.2);
    color: #4caf50;
}

.difficulty-badge[data-difficulty="band_2"] {
    background: rgba(139, 195, 74, 0.2);
    color: #8bc34a;
}

.difficulty-badge[data-difficulty="band_3"] {
    background: rgba(255, 235, 59, 0.2);
    color: #ffeb3b;
}

.difficulty-badge[data-difficulty="band_4"] {
    background: rgba(255, 152, 0, 0.2);
    color: #ff9800;
}

.difficulty-badge[data-difficulty="band_5"] {
    background: rgba(255, 87, 34, 0.2);
    color: #ff5722;
}

.difficulty-badge[data-difficulty="band_6"] {
    background: rgba(244, 67, 54, 0.2);
    color: #f44336;
}

.modal-footer {
    display: flex;
    gap: 12px;
    padding: 24px 30px;
    border-top: 1px solid rgba(var(--accent-rgb), 0.4);
    justify-content: flex-end;
}

.clear-btn {
    padding: 12px 24px;
    background: transparent;
    border: 1px solid rgba(var(--accent-rgb), 0.4);
    color: var(--accent);
    cursor: pointer;
    transition: all 0.3s;
    font-size: 14px;
}

.clear-btn:hover {
    background: var(--accent);
    color: var(--bg-primary);
}

.apply-btn {
    padding: 12px 32px;
    background: var(--text-secondary);
    border: none;
    color: var(--bg-primary);
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s;
    font-size: 14px;
}

.apply-btn:hover {
    background: var(--text-primary);
    box-shadow: 0 4px 12px rgba(254, 231, 179, 0.4);
}

::-webkit-scrollbar {
    width: 12px;
}

::-webkit-scrollbar-track {
    background: var(--bg-secondary);
}

::-webkit-scrollbar-thumb {
    background: var(--bg-secondary);
    border-radius: 6px;
}

::-webkit-scrollbar-thumb:hover {
    background: var(--accent);
}
</style>